import type React from 'react'
import { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'

import AddressEntryItem from '@/components/AddressItem'
import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import Contact from '@/components/Contact'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import {
  displayFailReasons,
  displaySuccessReasons,
} from '@/components/DecisionPanelCards/utils'
import DocumentRequests from '@/components/DocumentRequests'
import type { RefereeDetails } from '@/components/DocumentRequests/utils/types'
import ExpandableCard from '@/components/ExpandableCard'
import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import Lightbox, { getFile } from '@/components/Lightbox'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import ProsCons from '@/components/ProsCons/ProsCons'
import ReopenSection from '@/components/ReopenSection/ReopenSection'
import { SectionName } from '@/components/ReopenSection/types'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import type { Application, Address, Document } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { isFinalOutcome } from '@/utils/outcome'
import { hasResidentialData } from '@/utils/residential'
import { isRefCompleted } from '@/utils/subjectHelpers'
import { useUserTracking } from '@/utils/tracking'

import OverrideOutcomes from './OverrideOutcomes'
import Reset from './Reset'

interface Props {
  application: Application
  isLoadingTouchpoints: boolean
  subjectTouchpoints: Touchpoint[]
  fetchSubjectTouchpoints: () => Promise<void>
  isRestrictedReference: boolean
}

const ResidentialContainer = styled.div`
  h4 {
    margin: 0;
    padding-top: 20px;
  }

  .historyContainer {
    display: grid;
    grid-gap: 10px;
  }

  #forwardList {
    .addressDetails {
      display: none;
    }

    > div {
      > div {
        flex-flow: row;
      }
    }
  }
`

const Body = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
  transition: height 0.25s ease-in;
`

const ResidentialCard: React.FC<Props> = ({
  application,
  isLoadingTouchpoints,
  subjectTouchpoints,
  fetchSubjectTouchpoints,
  isRestrictedReference,
}) => {
  const outcomeSections = application?.outcome?.sections ?? []
  const residentialOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.residential,
  )
  const overallOutcomeStatus = application.outcome?.status
  const residentialOutcomeStatus = residentialOutcome?.status

  const [resEvidenceOpen, setResEvidenceOpen] = useState(false)
  const [startIndex, setStartIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)

  const { trackUserActivity } = useUserTracking()

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'residential',
  )

  function getProofMetaData(array1: Document[], filename: string): string | null {
    const filteredDocs = array1.filter(filteredItem => {
      return filteredItem.fileName === filename
    })
    if (filteredDocs.length > 0 && filteredDocs[0].specifiedType) {
      return filteredDocs[0].specifiedType
    }
    return null
  }

  const addressList = application?.context?.addresses ?? []

  const [residencyFail, setResidencyFail] = useState<string[]>([])

  const [residencySuccess, setResidencySuccess] = useState<string[]>([])

  useEffect(() => {
    if (
      application &&
      application.results !== undefined &&
      application.results !== null &&
      application.results.length > 0
    ) {
      setResidencySuccess(displaySuccessReasons(application.results, 'residential'))
      setResidencyFail(displayFailReasons(application.results, 'residential'))
    }
  }, [application, application.results])

  const current = addressList.filter(address => address.addressType === 'current')
  const previous = addressList.filter(address => address.addressType === 'previous')
  const financiallyAssociated = addressList.filter(
    address => address.addressType === 'financiallyAssociated',
  )
  const forwardingAddress = addressList.filter(
    address => address.addressType === 'forwarding',
  )

  const resEvidenceDocument = useMemo(() => {
    if (
      !application.context ||
      !application.context.addresses ||
      !(application.context.addresses.length > 0) ||
      !application.context.addresses[0].evidence ||
      !(application.context.addresses[0].evidence.length > 0)
    ) {
      return []
    }

    const resDocs = application.context.addresses[0]?.evidence ?? []

    const linkedDocs: Document[] = []

    resDocs.forEach(item => {
      if (application && application.context && application.context.documents) {
        const document = application.context?.documents.find(document =>
          document.fileName.includes(item),
        )
        if (document) linkedDocs.push(document)
      }
    })

    return linkedDocs
  }, [application])

  const hasLandlordReference = !!(current && current.length > 0 && current[0].reference)

  const landlordContactDetails: RefereeDetails | undefined =
    hasLandlordReference &&
    current[0].reference?.subjectId &&
    current[0].reference?.mobile
      ? {
          name: current[0].reference.name,
          email: current[0].reference.submittedBy?.authorEmail,
          mobile: current[0].reference.mobile,
        }
      : undefined

  const handleOpenResEvidence = (index: number) => {
    setStartIndex(index)
    setResEvidenceOpen(true)
    trackUserActivity('View document', {
      subjectId: application.subjectId,
      customerId: application.customerId,
      fileName: getFile(resEvidenceDocument[index]),
    })
  }

  return (
    <>
      {resEvidenceOpen && (
        <Lightbox
          images={resEvidenceDocument}
          onClose={() => setResEvidenceOpen(false)}
          startIndex={startIndex}
          customerId={application.customerId}
          subjectId={application.subjectId}
        />
      )}

      <CommentsSidebar
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="RESIDENTIAL"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />

      <ExpandableCard
        isExpanded={isExpanded}
        handleExpand={() => setIsExpanded(!isExpanded)}
        label="Residential"
        icon="residential"
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && residentialOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={residentialOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
        expandedContent={
          !hasResidentialData(application) ? (
            <>No data</>
          ) : (
            <Body>
              <ButtonsContainer>
                <Reset
                  application={application}
                  customerId={application.customerId}
                  applicationId={application.applicationId}
                  subjectId={application.subjectId}
                  disabled={isRestrictedReference}
                />
                <DocumentRequests
                  customerId={application.customerId}
                  applicationId={application.applicationId}
                  subjectId={application.subjectId}
                  sectionName={SectionName.residential}
                  subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
                  isRefCompleted={isRefCompleted(application)}
                  disabled={isRestrictedReference}
                  refereeDetails={landlordContactDetails}
                />
                <Contact
                  customerId={application.customerId}
                  sectionName="residential"
                  applicantDetails={{
                    contactName: application.name ?? 'Unknown',
                    subjectId: application.subjectId,
                    phoneNumber: application.context?.mobile ?? 'Unknown',
                  }}
                  agentDetails={{
                    contactName: application?.agent?.name ?? 'Unknown',
                    phoneNumber: application?.agent?.phone ?? 'Unknown',
                  }}
                  refereeDetails={
                    hasLandlordReference &&
                    current[0].reference?.subjectId &&
                    current[0].reference?.mobile
                      ? {
                          contactName: current[0].reference.name,
                          subjectId: current[0].reference.subjectId,
                          phoneNumber: current[0].reference.mobile,
                        }
                      : undefined
                  }
                  disabled={isRestrictedReference}
                />

                {application.applicationId &&
                  application.subjectId &&
                  isFinalOutcome(residentialOutcomeStatus) && (
                    <ReopenSection
                      customerId={application.customerId}
                      applicationId={application.applicationId}
                      subjectId={application.subjectId}
                      sectionName={SectionName.residential}
                      disabled={isRestrictedReference}
                    />
                  )}
              </ButtonsContainer>
              <div>
                <ResidentialContainer>
                  <ProsCons pros={residencySuccess} cons={residencyFail} />
                  <h2>Address History</h2>
                  <hr />
                  <div className="historyContainer">
                    {addressList.length > 0 ? (
                      <>
                        {
                          <>
                            {current.length > 0 ? (
                              current.map((address: Address, index: number) => {
                                return (
                                  <div id="currentList" key={index}>
                                    <AddressEntryItem
                                      addressObj={address}
                                      index={index}
                                      application={application}
                                      onOpenResEvidence={handleOpenResEvidence}
                                      getProofMetaData={getProofMetaData}
                                      isCurrentAddress
                                      subjectId={application.subjectId}
                                      customerId={application.customerId}
                                      residentialOutcomeStatus={residentialOutcomeStatus}
                                      isRestrictedReference={isRestrictedReference}
                                    />
                                  </div>
                                )
                              })
                            ) : (
                              <span className="noAddress">
                                No current address provided
                              </span>
                            )}
                          </>
                        }

                        {
                          <>
                            {previous.length > 0 ? (
                              previous.map((address: Address, index: number) => {
                                return (
                                  <div id="prevList" key={index}>
                                    <AddressEntryItem
                                      addressObj={address}
                                      index={index}
                                      application={application}
                                      onOpenResEvidence={handleOpenResEvidence}
                                      getProofMetaData={getProofMetaData}
                                      subjectId={application.subjectId}
                                      customerId={application.customerId}
                                      residentialOutcomeStatus={residentialOutcomeStatus}
                                      isRestrictedReference={isRestrictedReference}
                                    />
                                  </div>
                                )
                              })
                            ) : (
                              <span className="noAddress">
                                No previous address provided
                              </span>
                            )}
                          </>
                        }
                        {
                          <>
                            {financiallyAssociated.length > 0 ? (
                              financiallyAssociated.map(
                                (address: Address, index: number) => {
                                  return (
                                    <div id="finList" key={index}>
                                      <AddressEntryItem
                                        addressObj={address}
                                        index={index}
                                        application={application}
                                        onOpenResEvidence={handleOpenResEvidence}
                                        getProofMetaData={getProofMetaData}
                                        subjectId={application.subjectId}
                                        customerId={application.customerId}
                                        residentialOutcomeStatus={
                                          residentialOutcomeStatus
                                        }
                                        isRestrictedReference={isRestrictedReference}
                                      />
                                    </div>
                                  )
                                },
                              )
                            ) : (
                              <span className="noAddress">
                                No other financially linked addresses provided
                              </span>
                            )}
                          </>
                        }

                        {
                          <>
                            <span>
                              <strong>Forwarding Address</strong>
                            </span>
                            {forwardingAddress.length > 0 ? (
                              forwardingAddress.map((address: any, index: number) => {
                                return (
                                  <div id="forwardList" key={index}>
                                    <AddressEntryItem
                                      addressObj={address}
                                      index={index}
                                      application={application}
                                      onOpenResEvidence={handleOpenResEvidence}
                                      getProofMetaData={getProofMetaData}
                                      subjectId={application.subjectId}
                                      customerId={application.customerId}
                                      residentialOutcomeStatus={residentialOutcomeStatus}
                                      isRestrictedReference={isRestrictedReference}
                                    />
                                  </div>
                                )
                              })
                            ) : (
                              <span className="noAddress">
                                No forwarding address provided
                              </span>
                            )}
                          </>
                        }
                      </>
                    ) : (
                      <span>Nothing yet!</span>
                    )}
                  </div>
                </ResidentialContainer>
              </div>

              {!isRestrictedReference && (
                <OverrideOutcomes
                  subjectOutcome={application.outcome}
                  applicationId={application.applicationId}
                  customerId={application.customerId}
                  subjectId={application.subjectId}
                  hasLandlordReference={hasLandlordReference}
                />
              )}
            </Body>
          )
        }
      />
    </>
  )
}

export default ResidentialCard
